/* eslint-disable react-hooks/exhaustive-deps */
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useQueryClient } from '@tanstack/react-query';

import { useFlags } from 'launchdarkly-react-client-sdk';
// Imported from the leaf module, not the capability barrel: the barrel re-exports
// use-tier-delete-control.ts, which imports @providers/LineItemsProvider and would
// otherwise create a circular import back into this file.
import { invalidateTierConfigurationAfterSave } from '@capabilities/tiered-pricing/api/invalidate-after-save';
import { EventRequest, GlobalLotsUiConfigKeys, LineItem, LINE_ITEM_UPDATE_MECHANISM, NsbRowConfig } from 'types';
import { alertMessage, Global } from '@utils/common';
import {
  APPLY_BASELINE_CHANGES,
  EVENT_CHANGES,
  EVENT_SHELL_REFRESHED,
  FETCH_BASELINE_IN_PROGRESS,
  FETCH_BASELINE_IN_PROGRESS_END,
  FETCH_LINE_ITEMS,
  FINISH_PROGRESS,
  LINE_ITEMS_IMPORT_SAVE_FINISHED,
  LOT_CARD_HEADER_CHANGES,
  RESET_BASELINE_STATE,
  SHOULD_REFRESH,
  START_FETCH,
  START_PROGRESS,
  UPDATE_UI_CONFIG,
  UPDATE_ZERO_PRICED_LINE_ITEMS,
} from '@providers/LineItemsProvider/constants';
import {
  applyNsbCellsForSavedEvent,
  buildMissingNsbPriceChanges,
  computeManualEntrySpend,
  getBuyerPickedIds,
  hasBuyerPickedList,
  hydrateEventOverrideAllowed,
  hydrateNsbRowConfigs,
  hydrateNsbSelection,
  hydrateResolvedNamesForLineItems,
  isNsbApplied,
  isPendingIcoSource,
  ManualSpendSummary,
  NsbPersistedSelection,
  resetNsbEventState,
  setNsbEnabled,
  setUserProvided,
} from '@components/bid_request/details/LineItemsTable/SmartBaselineBenchmarkRenderer';
import { NsbNoBaselineWarningModal } from '@components/bid_request/details/NsbNoBaselineWarningModal';
import { UserContext } from '@components/providers/UserProvider/UserProvider';
import { persistNsbConfig } from '@api/NsbConfigApi';
import { isLineItemChanged, hasEventChanges, hasLineItemStateChanges } from '@components/bid_request/details/utils';
import { REQUEST_TYPE_PROPOSAL } from '@components/bid_request/RequestConstants';
import { hasErrors as checkForErrors, toggleErrorNotification } from '@components/bid_request/details/utils/validations';
import {
  countIdErrors,
  hasSentRowErrors,
  idErrorsStore,
  useInvalidateLotPagesOnShellRefresh,
  useLotsIdErrors,
  usePaginationEnabled,
} from '@features/buyer-line-items-pagination';
import { prepareUiConfigChanges } from '@components/bid_request/details/StickyHeader/EventConfigurationMenu/utils/prepareUiConfigChanges';
import { updateLineItemSettings, updateServeSingleSupplierQuoteFeedback, updateSupplierQuantityEnabled, updateUiConfig } from '@api/LotsApi';
import DatadogService from '@services/DatadogService';
import { beginLoad, reset } from '@providers/LineItemsProvider/utils/lineItemsPerf';
import { getSaveErrorMessage } from '@utils/getSaveErrorMessage';

import { getLineItemHistory } from '@api/LineItemHistoryApi';
import { lineItemsReducer } from './LineItemsReducer';
import { LotCardHeaderChangesAction } from './action_types';
import { errorMessages, loadEventAndLots, loadEventShell, saveEventData } from './EventsServices';
import { defaultLineItemsState } from './defaultLineItemsState';
// LineItemsContext and its Props type now live in ./LineItemsContext (a leaf) so that consumers
// can use the context without transitively pulling this provider module into their graph. Import
// LineItemsContext from '@providers/LineItemsProvider/LineItemsContext' or from the barrel
// ('@providers/LineItemsProvider'), NOT from this file.
import { ApplyLineItemSettingsPayload, LineItemsContext, UiConfigChanges } from './LineItemsContext';

interface LineItemsProviderProps {
  children: ReactNode;
  request: EventRequest;
}

// The legacy Smart Baseline fetch is off while one is in flight, without CSB, under NSB, and on a paginated event.
const isLegacyBaselineFetchOff = ({ inProgress, paginationEnabled, tlcNewSmartBaseline, tlcSmartBaseline }: {
  inProgress: boolean;
  paginationEnabled?: boolean;
  tlcNewSmartBaseline: boolean;
  tlcSmartBaseline: boolean;
}) => [inProgress, !tlcSmartBaseline, tlcNewSmartBaseline, paginationEnabled].some(Boolean);

export const LineItemsProvider = ({ request, children }: LineItemsProviderProps) => {
  const { tlcNewSmartBaseline = false, tlcSmartBaseline = false } = useFlags();
  const currentUser = useContext(UserContext);
  // NSB is restricted to RFP (proposal) events. Every NSB gate reads this compound value, so widening to
  // another event type later is a one-line OR here. Non-RFP falls back to the existing flag-off path.
  const nsbEnabled = tlcNewSmartBaseline && request?.request_type === REQUEST_TYPE_PROPOSAL;
  // Undefined until LaunchDarkly has identified the user; the load waits for it. Paginated needs the flag on
  // and New Smart Baseline off for this event, both read after identify.
  const flagRead = usePaginationEnabled(request?.id);
  const flagPaginationEnabled = Boolean(flagRead?.pagination)
    && !(flagRead?.newSmartBaseline && request?.request_type === REQUEST_TYPE_PROPOSAL);
  const bothFlagsOn = Boolean(flagRead?.pagination && flagRead.newSmartBaseline);
  const loggedBothOnEventId = useRef<EventRequest['id']>();
  const queryClient = useQueryClient();
  // The event whose read path is stored on state.event: its refreshes reuse that path, another event reads the flag.
  const pathEventId = useRef<EventRequest['id']>();

  useEffect(() => {
    if (!bothFlagsOn || request?.id === undefined || loggedBothOnEventId.current === request.id) return;

    loggedBothOnEventId.current = request.id;
    DatadogService.captureErrorMessage(
      'buyer-line-items-pagination and tlc-new-smart-baseline are both on',
      { eventId: request.id },
    );
  }, [bothFlagsOn, request?.id]);

  // Publish that gate to the NSB apply store, which every other NSB caller goes through. The import
  // reducers in actions.ts are pure and cannot read a LaunchDarkly flag, so this is where the flag
  // reaches them: with NSB off the store refuses every mutation and the cells stay installed, which is
  // the pre-branch behaviour. Runs before the data-load effect below, which may apply cells.
  useEffect(() => { setNsbEnabled(nsbEnabled); }, [nsbEnabled]);
  const { t } = useTranslation('common');
  const [hasErrors, setHasErrors] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);

  const [state, dispatch] = useReducer(lineItemsReducer, defaultLineItemsState);
  const lotsIdErrors = useLotsIdErrors();

  // NSB: wipe event-scoped singletons whenever the event changes. They are module-level and survive
  // SPA navigation, so without this a new event inherits the previous event's resolved item matches
  // and method/flavor selections. Declared first so it runs before the data-load effect below.
  // The id-keyed cell errors are module-level too.
  useEffect(() => {
    resetNsbEventState();
    reset();
    idErrorsStore.clearAll();
    return reset;
  }, [request?.id]);

  useEffect(() => {
    if (state.event.paginationEnabled) return;
    const { lotsErrors } = state;

    setHasErrors(checkForErrors(lotsErrors));
    // TLC: Need to add `Highlight errors` button here.
    const description = t('bid_request.line_items.validation.description');

    toggleErrorNotification({ description, lotsErrors, t });
  }, [state.lotsErrors]);

  useEffect(() => {
    if (!state.event.paginationEnabled) return;
    const description = t('bid_request.line_items.validation.description');

    toggleErrorNotification({ count: countIdErrors(lotsIdErrors), description, t });
  }, [lotsIdErrors]);

  useEffect(() => {
    if (!state.event.paginationEnabled) return;
    const { changes, originalLots } = state;

    setHasErrors(hasSentRowErrors({ changes, lotsIdErrors, originalLots }));
  }, [state.changes, lotsIdErrors, state.originalLots, state.event.paginationEnabled]);

  useEffect(() => {
    if (!request?.state || flagRead === undefined) return;

    if (state.shouldRefresh) {
      const fetchEvent = async () => {
        dispatch({ type: START_FETCH });
        beginLoad(request.id);
        let event: EventRequest = { ...request };
        let shell: EventRequest | undefined;
        const isSameEvent = pathEventId.current === request.id;
        const loadPaginated = isSameEvent ? Boolean(state.event.paginationEnabled) : flagPaginationEnabled;
        const isShellRefresh = loadPaginated && isSameEvent;
        pathEventId.current = request.id;

        try {
          event = loadPaginated
            ? await loadEventShell({ queryClient, request, t })
            : await loadEventAndLots({ request, t });
          shell = event;
          // NSB: seed the durable per-row resolutions from what the server returned (the
          // resolved_line_item_name column) BEFORE the rows render. A row that already carries a
          // resolution then skips Part 1's match search on this load and comes straight back as
          // "See Intel" + Smart Baseline. Synchronous here (not an effect) so it lands before the
          // child cell renderers mount and fire their searches. Authoritative: clears any stale
          // resolutions from a previous event/load and reseeds from this payload.
          hydrateResolvedNamesForLineItems((event?.lots ?? []).flatMap(lot =>
            (lot.line_items ?? []).map(li => ({
              inputName: li.name,
              lineItemId: li.id,
              resolvedName: li.resolved_line_item_name,
            }))));

          // NSB: restore the event-level method/flavor selections + override toggles persisted in
          // ui_config.smart_baseline, BEFORE the rows render — so the dropdowns come back as set and the rows
          // re-derive from the saved selection instead of snapping to defaults.
          const nsb = (event as { ui_config?: { smart_baseline?: Record<string, unknown> } })?.ui_config?.smart_baseline;
          if (nsb) {
            hydrateNsbSelection(nsb as NsbPersistedSelection);
            if (typeof nsb.allow_negotiation_override === 'boolean') hydrateEventOverrideAllowed('negotiation', nsb.allow_negotiation_override);
            if (typeof nsb.allow_baseline_override === 'boolean') hydrateEventOverrideAllowed('baseline', nsb.allow_baseline_override);
          }

          // NSB: restore each row's per-row override (nsb_row_config) — a manual entry or a per-row
          // flavor pick. This both repaints the right cell label and makes the auto-seed effects skip
          // those rows so their saved price isn't clobbered back to the event default on reload.
          // NSB: re-populate the write-once is_buyer_provided_baseline_price flags from the persisted column, BEFORE
          // rows render, so the seeder skips baselining user-provided rows on reload (their saved price stands).
          (event?.lots ?? []).forEach(lot => (lot.line_items ?? []).forEach(li => {
            if (li.is_buyer_provided_baseline_price) setUserProvided(`${event?.id}_${li.id}`);
          }));
          // Only an event with NO list at all falls back to treating every sourced row as the buyer's.
          // An event carrying an empty list means the buyer picked nothing, which is not the same thing.
          const buyerPicked = getBuyerPickedIds();
          const hasPickedList = hasBuyerPickedList();
          // The backend's ICO fallback on an unpriced row is not a decision, so a smart baseline match may
          // replace it — unless the buyer explicitly picked ICO for that row. Draft events only: once an
          // event is live the fallback is committed and ICO fills the row after round 1.
          const isDraftEvent = event?.state === Global.requests.states.draft;
          const savedSources = (event?.lots ?? []).flatMap(lot =>
            (lot.line_items ?? [])
              .filter(li => li.baseline_price_source || li.negotiation_price_source)
              .map(li => {
                const explicitlyPicked = hasPickedList && buyerPicked.has(String(li.id));
                const icoFallback = isDraftEvent
                  && !explicitlyPicked
                  && isPendingIcoSource(li.negotiation_price_source, li.negotiation_starting_price);

                return {
                  baselinePrice: li.price,
                  buyerPicked: !icoFallback && (!hasPickedList || explicitlyPicked),
                  config: {
                    baseline: li.baseline_price_source ?? undefined,
                    negotiation: li.negotiation_price_source ?? undefined,
                  } as NsbRowConfig,
                  icoFallback,
                  negotiationPrice: li.negotiation_starting_price,
                  sbKey: `${event?.id}_${li.id}`,
                };
              }));
          hydrateNsbRowConfigs(savedSources);
          // A row only carries a source if this event was SAVED with baselines, so this is the one place
          // that knows the difference between "already done" and "never matched". Arriving at an event
          // withholds the cells and offers the match; an event that has been saved has nothing to offer,
          // so put its cells straight in and let the buyer adjust individual rows from the popups. Never over
          // a pending offer, though: an import reloads here before the buyer has answered its Apply offer.
          if (nsbEnabled && savedSources.length > 0) applyNsbCellsForSavedEvent();
        } catch (error) {
          const errorMessage: string = error instanceof Error ? error.message : String(error);
          alertMessage(errorMessage, 'error', 5);
          DatadogService.captureErrorMessage(`Failed to load event data in useEffect with error message - ${errorMessage}`);
        } finally {
          if (isShellRefresh) {
            dispatch({ payload: { event: shell }, type: EVENT_SHELL_REFRESHED });
          } else {
            dispatch({ payload: { event, paginationEnabled: loadPaginated, t }, type: FETCH_LINE_ITEMS });
          }
        }
      };

      fetchEvent();
    } else {
      // Making sure we have the latest UI config state based of request values on each page load
      dispatch({ payload: { request }, type: EVENT_CHANGES });
    }
  }, [request?.id, request?.currency, request?.state, request?.active_bids, state.shouldRefresh, flagRead]);

  useInvalidateLotPagesOnShellRefresh(request?.id, state.shellRefreshCount);

  const handleFetchSmartBaselines = async () => {
    // CSB <-> NSB mutual exclusivity: when NSB is on, the CSB baseline populator must NOT run — it
    // fetches recent_price and writes it into the same NSP/BPU cells NSB manages, which would clobber
    // the NSB-applied prices. NSB wins. (Operationally CSB + NSB are never both on per customer; this
    // is the code-level safety net so an accidental double-on can't corrupt data.)
    if (!tlcSmartBaseline || tlcNewSmartBaseline) return;

    const { lots } = state;
    const zeroPricedLineItems: Partial<LineItem>[] = [];

    lots?.forEach(lot => lot.line_items.forEach(li => {
      if (Number(li.price) === 0 || Number(li.negotiation_starting_price) === 0) {
        zeroPricedLineItems.push(li);
      }
    }));

    if (zeroPricedLineItems.length === 0) {
      dispatch({ type: RESET_BASELINE_STATE });
      return;
    }

    // update the count of eligible zero-priced line items.
    dispatch({
      payload: {
        zeroPricedLineItemsCount: zeroPricedLineItems.length
      },
      type: UPDATE_ZERO_PRICED_LINE_ITEMS
    });

    dispatch({
      type: FETCH_BASELINE_IN_PROGRESS
    });

    try {
      const lineItemNames: string[] = [];
      zeroPricedLineItems.forEach(li => lineItemNames.push(li.name));

      if (lineItemNames.length === 0) {
        dispatch({ type: FETCH_BASELINE_IN_PROGRESS_END });
        return;
      }

      const baselines = await getLineItemHistory(lineItemNames, request.id);

      const baselineAppliedLineItems = baselines.line_item_history
        .map((lineItem: { id: number; lot_id: number; recent_price: { price_per_unit: number } | null }) => {
          const { id, lot_id: lotId } = lineItem;
          // Handle null recent_price - use 0 as default or skip this item
          const baseLinePrice = lineItem.recent_price?.price_per_unit ?? 0;

          return {
            baseLinePrice,
            hasRecentPrice: lineItem.recent_price !== null,
            id,
            lotId
          };
        })
        .filter((item: { baseLinePrice: number; hasRecentPrice: boolean; id: number; lotId: number }) =>
          item.hasRecentPrice); // Only include items that actually have recent price data

      // Show no baselines found alert
      if (baselineAppliedLineItems.length === 0) {
        dispatch({
          payload: {
            showZeroBaselinesAlert: true
          },
          type: FETCH_BASELINE_IN_PROGRESS_END
        });
        return;
      }

      dispatch({
        payload: {
          baselineAppliedLineItems,
        },
        type: APPLY_BASELINE_CHANGES
      });
    } catch (error) {
      const errorMessage: string = error instanceof Error ? error.message : String(error);

      alertMessage(errorMessage, 'error', 5);
      DatadogService.captureErrorMessage(`Failed to fetch baselines with error message - ${errorMessage}`);
      dispatch({ type: FINISH_PROGRESS });
    }
  };

  useEffect(() => {
    const { baselineState: { fetchBaselines, inProgress }, changes, event, lotsErrors, isLoading, isInProgress } = state;

    if (isLegacyBaselineFetchOff({ inProgress, paginationEnabled: event.paginationEnabled, tlcNewSmartBaseline, tlcSmartBaseline })) return;

    const isLotChangesEmpty = Object.keys(changes).length === 0;
    const hasNoErrors = !checkForErrors(lotsErrors);
    // extra protection to ensure validating, saving, and fetching events are completed prior to attempting fetching and applying baselines.
    // Should help ensure that UI is ready state to begin applying baselines on slower 95% events.
    const uiReadyState = isLotChangesEmpty && hasNoErrors && !isLoading && !isInProgress;

    if (fetchBaselines && uiReadyState) {
      handleFetchSmartBaselines();
    }
  }, [state.baselineState.fetchBaselines, state.changes, state.lotsErrors, state.isLoading, state.isInProgress, tlcNewSmartBaseline, tlcSmartBaseline]);

  // Pre-submit warning state: non-null while the manual-entry-baseline warning modal is open.
  const [noBaselineWarning, setNoBaselineWarning] = useState<ManualSpendSummary | null>(null);
  // The actual save (no warning gate) — called directly when there's nothing to warn about, or from
  // the pre-submit warning modal's Proceed.
  //
  // The Smart Baseline backfill (prices for rows that were never rendered — see nsbSaveBackfill) is
  // derived HERE, inside the save, and deliberately AFTER START_PROGRESS. Deriving it earlier opened a
  // window: this callback closes over `state`, so an edit landing between the Save click and the
  // flavours returning produced a newer `doSaveChanges` while the in-flight one still held the older
  // `state.changes` — a silently dropped edit. Dispatching first means whatever gates on
  // `state.progress` also covers the derive, and the derive and the save now read one snapshot.
  //
  // Merged rather than dispatched: the save reads state synchronously, so a dispatch would not be
  // visible to it, and the post-save refetch brings the written values back to the screen anyway.
  //
  // Only once the cells are applied: withheld cells mean the buyer has not accepted the smart baselines
  // (an upload's Apply offer is still open, or it was declined), and a save must not apply them instead.
  const doSaveChanges = useCallback(async () => {
    if (hasSuccess) return;

    dispatch({ type: START_PROGRESS });
    // Read in the finally block below, which runs on both outcomes.
    let saveSucceeded = false;
    try {
      const backfill = nsbEnabled && isNsbApplied()
        ? await buildMissingNsbPriceChanges(state.lots ?? [], request.id)
        : undefined;
      const { baselineState: { baselinesApplied }, event, changes: stateChanges, lots } = state;
      // An existing entry always wins: the buyer's own edit is never overwritten by a derived price.
      const lotChanges = backfill
        ? Object.entries(backfill).reduce((acc, [lotId, byLineItem]) => {
          const lot = acc[lotId] || ({ id: lotId } as (typeof acc)[string]);
          const lineItems = { ...(lot.line_items || {}) };
          Object.entries(byLineItem).forEach(([lineItemId, columns]) => {
            lineItems[lineItemId] = { ...columns, ...lineItems[lineItemId], id: lineItemId };
          });
          return { ...acc, [lotId]: { ...lot, line_items: lineItems } };
        }, { ...stateChanges })
        : stateChanges;
      const mechanism = state.importState.status.saveLineItemsAfterImport ? LINE_ITEM_UPDATE_MECHANISM.IMPORT : undefined;

      await saveEventData({ event, id: request.id, lotChanges, lots, mechanism, nsbEnabled, t });
      saveSucceeded = true;

      invalidateTierConfigurationAfterSave(queryClient, request.id);

      // This save already persisted everything in state.changes, including any
      // new lot, so the refetch triggered here must NOT re-merge stale
      // `new-<uuid>` changes entries (see handleFetchLineItems / PEPPERMINT-2803).
      dispatch({ payload: { preserveNewLots: false }, type: SHOULD_REFRESH });

      // Reset baseline state only if baselines have already been applied. Otherwise we need that state to run the baseline calcs after line item import save.
      if (baselinesApplied) {
        dispatch({ type: RESET_BASELINE_STATE });
      }
      setHasSuccess(true);

      // Reset hasSuccess after 3 seconds
      setTimeout(() => {
        setHasSuccess(false);
      }, 3000);
    } catch (error) {
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      alertMessage(errorMessage, 'error', 5);
      if (errorMessage.indexOf('Lot Name can\'t be blank') >= 0 ||
        errorMessage.indexOf('Line Items can\'t be blank') >= 0) {
        return;
      }
      DatadogService.captureErrorMessage(`Failed to save event data with error message - ${errorMessage}`);
    } finally {
      dispatch({ type: FINISH_PROGRESS });
      // Always clear the import-driven save flag, regardless of save outcome. If we left it set on
      // failure the next manual grid save would inherit mechanism='import' and write a spurious
      // BR-level import audit row. `success` gates only the `changes` clear — see the handler.
      dispatch({ payload: { success: saveSucceeded }, type: LINE_ITEMS_IMPORT_SAVE_FINISHED });
    }
  }, [
    hasSuccess,
    state.baselineState.baselinesApplied,
    state.event,
    state.changes,
    state.lots,
    request.id,
    t,
    state.importState.status.saveLineItemsAfterImport,
    queryClient
  ]);

  // Save Changes: when any line item uses a manual-entry baseline (no match or a manual override),
  // first show the pre-submit warning (spend excluded from savings analysis + an optional reason);
  // otherwise save straight through.
  const handleSaveChanges = useCallback(async (options?: { skipBaselineWarning?: boolean }) => {
    if (hasSuccess) return;
    // The pre-submit warning is only for an explicit "Save Changes" click — NOT the post-import
    // auto-save (which would otherwise pop the warning the moment CSV items are uploaded).
    if (nsbEnabled && !options?.skipBaselineWarning) {
      // A pre-backfill snapshot, and honestly so: these are the rows that are blank on screen right now.
      // The backfill is derived inside doSaveChanges, so the modal's numbers can differ from what is
      // ultimately written — the warning is about the buyer's own manual entries, not the derived fills.
      const summary = computeManualEntrySpend(
        (state.lots ?? []).flatMap(lot => (lot.line_items ?? []).map(li => ({
          extendedQuantity: li.extended_quantity,
          id: li.id,
          name: li.name,
          price: li.price,
        }))),
        request.id,
      );
      if (summary.manualCount > 0) {
        setNoBaselineWarning(summary);
        return;
      }
      await doSaveChanges();
      return;
    }
    await doSaveChanges();
  }, [hasSuccess, state.lots, request.id, doSaveChanges]);

  // Warning modal: Proceed records the (optional) reason in ui_config and continues the save; Cancel
  // just closes (the buyer can go fix baselines first).
  const handleNoBaselineProceed = useCallback((reason?: string) => {
    setNoBaselineWarning(null);
    if (reason) persistNsbConfig(request.id, { no_baseline_reason: reason }).catch(() => undefined);
    doSaveChanges();
  }, [request.id, doSaveChanges]);
  const handleNoBaselineCancel = useCallback(() => {
    setNoBaselineWarning(null);
  }, []);

  const handleUpdateUiConfig = useCallback(async (changes: UiConfigChanges) => {
    dispatch({ type: START_PROGRESS });
    try {
      const { showPricingColumns } = state.event;
      const uiConfig = prepareUiConfigChanges({ changes, showPricingColumns });

      if (!uiConfig) return;
      const response = await updateUiConfig(request.id, uiConfig);

      if (!response?.data) {
        throw new Error(errorMessages({ prefix: t('line_items.errors.saving'), t }));
      }

      request.setRequestProperty({ ui_config: response.data });

      // Update the UI config in the state
      dispatch({ payload: { uiConfig }, type: UPDATE_UI_CONFIG });
      alertMessage(t('messages.saved'), 'success', 5);
    } catch (error) {
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      alertMessage(errorMessage, 'error', 5);
      DatadogService.captureErrorMessage(`Failed to update UI config with error message - ${errorMessage}`);
    } finally {
      dispatch({ type: FINISH_PROGRESS });
    }
  }, [state.event, request.id, request.setRequestProperty, t]);

  const handleUpdateSupplierQuantityEnabled = useCallback(async (value: boolean) => {
    dispatch({ type: START_PROGRESS });
    try {
      await updateSupplierQuantityEnabled(request.id, value);
      request.setRequestProperty({ supplier_quantity_enabled: value });
      alertMessage(t('messages.saved'), 'success', 5);
    } catch (error) {
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      alertMessage(errorMessage, 'error', 5);
      DatadogService.captureErrorMessage(`Failed to update supplier_quantity_enabled: ${errorMessage}`);
    } finally {
      dispatch({ type: FINISH_PROGRESS });
    }
  }, [request.id, request.setRequestProperty, t]);

  const handleApplyLineItemSettings = useCallback(async ({
    anchorPricingDisabled,
    serveSingleSupplierQuoteFeedback,
    supplierQuantityEnabled,
    uiConfigAnchorPrices,
    uiConfigLots,
  }: ApplyLineItemSettingsPayload): Promise<void> => {
    const hasLotsChanges = uiConfigLots && Object.keys(uiConfigLots).length > 0;
    const hasAnchorPricesChanges = uiConfigAnchorPrices && Object.keys(uiConfigAnchorPrices).length > 0;
    const hasUiConfigChanges = hasLotsChanges || hasAnchorPricesChanges;
    const hasSupplierQuantityChange = supplierQuantityEnabled !== undefined;
    const hasAnchorPricingDisabledChange = anchorPricingDisabled !== undefined;
    const hasServeSingleSupplierQuoteFeedbackChange = serveSingleSupplierQuoteFeedback !== undefined;
    if (
      !hasUiConfigChanges && !hasSupplierQuantityChange && !hasAnchorPricingDisabledChange
      && !hasServeSingleSupplierQuoteFeedbackChange
    ) return;

    dispatch({ type: START_PROGRESS });
    try {
      const payload: Record<string, unknown> = {};
      // The BE persists whatever ui_config hash we send by replacing the JSONB
      // column wholesale. To avoid dropping any sibling keys (lots.*, smart_baseline,
      // supplier_view, etc.), send the full current ui_config with our lots/anchor_prices
      // overrides merged in.
      const currentUiConfig = request.ui_config || {};
      const currentLots = currentUiConfig.lots || {};
      const currentAnchorPrices = currentUiConfig.anchor_prices || {};
      const mergedLots = hasLotsChanges ? { ...currentLots, ...uiConfigLots } : currentLots;
      const mergedAnchorPrices = hasAnchorPricesChanges
        ? { ...currentAnchorPrices, ...uiConfigAnchorPrices }
        : currentAnchorPrices;
      const mergedUiConfig: Record<string, unknown> = { ...currentUiConfig, lots: mergedLots };
      // Only inject anchor_prices when it actually changed. If unchanged, the ...currentUiConfig
      // spread already preserves whatever the BE persisted.
      if (hasAnchorPricesChanges) mergedUiConfig.anchor_prices = mergedAnchorPrices;
      if (hasUiConfigChanges) {
        payload.ui_config = mergedUiConfig;
      }
      if (hasSupplierQuantityChange) payload.supplier_quantity_enabled = supplierQuantityEnabled;
      if (hasAnchorPricingDisabledChange) payload.anchor_pricing_disabled = anchorPricingDisabled;
      if (hasServeSingleSupplierQuoteFeedbackChange) payload.serve_single_supplier_quote_feedback = serveSingleSupplierQuoteFeedback;

      const response = await updateLineItemSettings(request.id, payload);
      if (!response?.data) {
        throw new Error(errorMessages({ prefix: t('line_items.errors.saving'), t }));
      }

      const requestUpdate: Record<string, unknown> = {};
      if (hasUiConfigChanges) {
        requestUpdate.ui_config = mergedUiConfig;
      }
      if (hasSupplierQuantityChange) requestUpdate.supplier_quantity_enabled = supplierQuantityEnabled;
      if (hasAnchorPricingDisabledChange) requestUpdate.anchor_pricing_disabled = anchorPricingDisabled;
      if (hasServeSingleSupplierQuoteFeedbackChange) requestUpdate.serve_single_supplier_quote_feedback = serveSingleSupplierQuoteFeedback;
      request.setRequestProperty(requestUpdate);

      if (hasLotsChanges && uiConfigLots) {
        dispatch({
          payload: { uiConfig: uiConfigLots as Record<keyof GlobalLotsUiConfigKeys, boolean> },
          type: UPDATE_UI_CONFIG,
        });
      }
      alertMessage(t('messages.saved'), 'success', 5);
    } catch (error) {
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      alertMessage(getSaveErrorMessage(error, errorMessage), 'error', 5);
      DatadogService.captureErrorMessage('[LineItemsProvider] Failed to apply line item settings', {
        error: errorMessage,
        eventId: request.id,
        userId: currentUser?.id,
      });
      throw error;
    } finally {
      dispatch({ type: FINISH_PROGRESS });
    }
  }, [currentUser?.id, request, t]);

  const handleUpdateServeSingleSupplierQuoteFeedback = useCallback(async (value: boolean) => {
    dispatch({ type: START_PROGRESS });
    try {
      await updateServeSingleSupplierQuoteFeedback(request.id, value);
      request.setRequestProperty({ serve_single_supplier_quote_feedback: value });
      alertMessage(t('messages.saved'), 'success', 5);
    } catch (error) {
      const errorMessage: string = error instanceof Error ? error.message : String(error);
      alertMessage(errorMessage, 'error', 5);
      DatadogService.captureErrorMessage(`Failed to update serve_single_supplier_quote_feedback: ${errorMessage}`);
    } finally {
      dispatch({ type: FINISH_PROGRESS });
    }
  }, [request.id, request.setRequestProperty, t]);

  const hasChanges = useMemo(() => {
    return hasEventChanges({ event: state.event }) || hasLineItemStateChanges(state, isLineItemChanged);
  }, [
    state.changes,
    state.originalLots,
    state.event.currency,
    state.event.originalCurrency,
    state.event.eventIncumbentId,
    state.event.originalEventIncumbentId,
  ]);

  const handleChangeLot = useCallback((payload: LotCardHeaderChangesAction['payload']) => {
    dispatch({ payload, type: LOT_CARD_HEADER_CHANGES });
  }, [dispatch]);

  // Memoize context value to prevent unnecessary re-renders of all lots
  const value = useMemo(() => ({
    dispatch,
    handleApplyLineItemSettings,
    handleChangeLot,
    handleSaveChanges,
    handleUpdateServeSingleSupplierQuoteFeedback,
    handleUpdateSupplierQuantityEnabled,
    handleUpdateUiConfig,
    hasChanges,
    hasErrors,
    hasSuccess,
    nsbEnabled,
    request,
    state,
  }), [
    dispatch,
    handleApplyLineItemSettings,
    handleChangeLot,
    handleSaveChanges,
    handleUpdateServeSingleSupplierQuoteFeedback,
    handleUpdateSupplierQuantityEnabled,
    handleUpdateUiConfig,
    hasChanges,
    hasErrors,
    hasSuccess,
    nsbEnabled,
    request,
    // Only include state properties that actually affect the context consumers
    state.lots,
    state.changes,
    state.event,
    state.isLoading,
    state.isInProgress,
    state.lotsErrors,
    state.lotsPagination,
    state.originalLots,
    state.shellRefreshCount,
    state.baselineState,
    state.importState,
  ]);

  return (
    <LineItemsContext.Provider value={value}>
      {children}
      <NsbNoBaselineWarningModal currency={state.event.currency} onCancel={handleNoBaselineCancel} onProceed={handleNoBaselineProceed} summary={noBaselineWarning} />
    </LineItemsContext.Provider>
  );
};
